import { useEffect, useRef, useState } from "react";
import type { Book as EpubBook } from "epubjs";
import { searchBook, type SearchHit } from "@/services/searchService";

interface Props {
  book: EpubBook;
  /** Optional map of href → chapter label, used to enrich results. */
  hrefLabels?: Map<string, string>;
  onJumpTo: (cfi: string) => void;
  onClose: () => void;
  /** Render inside the bottom bar's panel slot instead of as a
   *  floating overlay: no fixed positioning, no scrim, inherits the
   *  bar's background. 2026-10-06 — Thomas asked for panels to sit on
   *  top of the progress row as part of the bar assembly. */
  inline?: boolean;
}

/**
 * Bottom-anchored single-line in-book search.
 *
 * Replaces the old right-side drawer per the 2026-06-14 redesign. The
 * bar floats just above the icon row at the bottom of the screen — single
 * input line, prev/next arrows to step through hits, the currently-focused
 * hit's excerpt previewed above the input. Tap a hit excerpt to jump to
 * it; the bar stays open so you can keep stepping through results.
 *
 * Closing dismisses the bar; the trigger remains the 🔎 icon on the
 * bottom bar.
 */
export function SearchPanel({ book, hrefLabels, onJumpTo, onClose, inline }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Focus the input on open. Brief delay so iOS Safari registers the
    // touch/click that opened us and doesn't reject focus inside a
    // raised-keyboard race.
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setCursor(0);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setBusy(true);
      try {
        const hits = await searchBook(book, query, { maxResults: 100 });
        if (!cancelled) {
          const enriched = hits.map((h) => ({
            ...h,
            chapterLabel: hrefLabels?.get(h.href.split("#")[0]),
          }));
          setResults(enriched);
          setCursor(0);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query, book, hrefLabels]);

  const current = results[cursor];

  const step = (delta: number) => {
    if (results.length === 0) return;
    const next = (cursor + delta + results.length) % results.length;
    setCursor(next);
    const hit = results[next];
    if (hit) onJumpTo(hit.cfi);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (results.length > 0) {
        // Enter jumps to the first / next hit.
        if (cursor === 0 && current) onJumpTo(current.cfi);
        else step(1);
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <div
      className={inline ? "pv-fg flex min-h-0 w-full flex-col overflow-y-auto" : "pv-surface pv-border pv-fg fixed inset-x-0 z-30 rounded-lg border"}
      style={{
        // Float just above the bottom button bar.
        //
        // 2026-10-06: this used to be a hard-coded 84px, with a comment
        // estimating the bar at "~72px". The bar MEASURES 151px today, so
        // the panel has been sitting on top of the bar rather than above
        // it — which is why Thomas asked for search to "appear above the
        // bar" even though this was already trying to. Now it reads the
        // bar's real measured height, published by ReaderView as
        // --pv-bar-h, so it can't drift out of date again.
        ...(inline ? {} : { bottom: "calc(var(--pv-bar-h, 152px) + 8px)" }),
        paddingLeft: "calc(env(safe-area-inset-left, 0px) + 8px)",
        paddingRight: "calc(env(safe-area-inset-right, 0px) + 8px)",
        paddingTop: "8px",
        paddingBottom: "8px",
      }}
    >
      {/* Excerpt preview — only when there's a current hit. Keeps the bar
          tiny when empty so it doesn't cover the reading area. */}
      {current && (
        <button
          onClick={() => onJumpTo(current.cfi)}
          className="pv-fg-soft mb-1.5 block w-full truncate text-left leading-tight" style={{ fontSize: "14px" }}
        >
          {current.chapterLabel && (
            <span className="pv-fg-soft mr-2 text-[10px]">
              {current.chapterLabel} ·
            </span>
          )}
          …{current.excerpt}…
        </button>
      )}

      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search this book…"
          className="pv-surface-2 pv-border pv-fg w-full flex-1 rounded border px-3 outline-none"
          style={{ fontSize: "18px", minHeight: "52px" }}
          inputMode="search"
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
        />
        <div className="pv-fg-soft min-w-[3.5rem] text-right" style={{ fontSize: "14px" }}>
          {busy
            ? "…"
            : results.length === 0 && query.trim().length >= 2
              ? "0 hits"
              : results.length > 0
                ? `${cursor + 1}/${results.length}`
                : ""}
        </div>
        <button
          onClick={() => step(-1)}
          disabled={results.length === 0}
          aria-label="Previous match"
          className="pv-border rounded border disabled:opacity-40"
          style={{ minWidth: "52px", minHeight: "52px", fontSize: "20px" }}
        >
          ↑
        </button>
        <button
          onClick={() => step(1)}
          disabled={results.length === 0}
          aria-label="Next match"
          className="pv-border rounded border disabled:opacity-40"
          style={{ minWidth: "52px", minHeight: "52px", fontSize: "20px" }}
        >
          ↓
        </button>
        {/* 2026-10-07: close X removed (Thomas). The Search tile toggles
            this panel shut, and the hide caret collapses the bar. */}
      </div>
    </div>
  );
}
